// Copyright 2025, University of Maryland, All Rights Reserved
import { AxiosInstance } from 'axios';
import { MonthlyStats, RecentJobsResponse, RecentJob } from '../types/job';

export interface JobContext {
  org_id?: string
  ws_id?: string
}

export const createJobsApi = (api_instance: AxiosInstance) => ({
  // Get job function
  getJob: async (job_id: string): Promise<RecentJob> => {
    const response = await api_instance.get<RecentJob>(`/jobs/${job_id}`)
    return response.data
  },

  // Get organization-level job
  getOrgJob: async (org_id: string, job_id: string): Promise<RecentJob> => {
    const response = await api_instance.get<RecentJob>(`/org/${org_id}/jobs/${job_id}`)
    return response.data
  },

  // Get workspace-level job
  getWorkspaceJob: async (org_id: string, ws_id: string, job_id: string): Promise<RecentJob> => {
    const response = await api_instance.get<RecentJob>(`/org/${org_id}/ws/${ws_id}/jobs/${job_id}`)
    return response.data
  },

  // Utility function to get job based on context
  getJobByContext: async ({ org_id, ws_id }: JobContext, job_id: string): Promise<RecentJob> => {
    if (ws_id && org_id) {
      const response = await api_instance.get<RecentJob>(`/org/${org_id}/ws/${ws_id}/jobs/${job_id}`)
      return response.data
    } else if (org_id) {
      const response = await api_instance.get<RecentJob>(`/org/${org_id}/jobs/${job_id}`)
      return response.data
    } else {
      const response = await api_instance.get<RecentJob>(`/jobs/${job_id}`)
      return response.data
    }
  },

  // Get monthly stats
  getMonthlyStats: async (month: string, process_type?: string): Promise<MonthlyStats> => {
    const url = process_type 
      ? `/jobs/stats/monthly/${month}?process_type=${process_type}`
      : `/jobs/stats/monthly/${month}`
    const response = await api_instance.get(url)
    return response.data
  },

  // Get monthly stats for organization
  getMonthlyOrgStats: async (org_id: string, month: string, process_type?: string): Promise<MonthlyStats> => {
    const url = process_type 
      ? `/org/${org_id}/jobs/stats/monthly/${month}?process_type=${process_type}`
      : `/org/${org_id}/jobs/stats/monthly/${month}`
    const response = await api_instance.get(url)
    return response.data
  },

  // Get monthly stats for workspace
  getMonthlyWorkspaceStats: async ( org_id: string, ws_id: string, month: string, process_type?: string): Promise<MonthlyStats> => {
    const url = process_type 
      ? `/org/${org_id}/ws/${ws_id}/jobs/stats/monthly/${month}?process_type=${process_type}`
      : `/org/${org_id}/ws/${ws_id}/jobs/stats/monthly/${month}`
    const response = await api_instance.get(url)
    return response.data
  },

  // Utility function to get monthly stats based on context
  getMonthlyStatsByContext: async ({ org_id, ws_id }: JobContext, month: string, process_type?: string): Promise<MonthlyStats> => {
    if (ws_id && org_id) {
      return createJobsApi(api_instance).getMonthlyWorkspaceStats(org_id, ws_id, month, process_type)
    } else if (org_id) {
      return createJobsApi(api_instance).getMonthlyOrgStats(org_id, month, process_type)
    } else {
      return createJobsApi(api_instance).getMonthlyStats(month, process_type)
    }
  },

  // Get recent jobs functions
  getRecentJobs: async ( limit: number = 500, month?: string): Promise<RecentJobsResponse> => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month) params.append('month', month)
    const response = await api_instance.get(`/jobs/recent?${params.toString()}`)
    return response.data
  },

  getRecentOrgJobs: async (org_id: string, limit: number = 500, month?: string): Promise<RecentJobsResponse> => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month) params.append('month', month)
    const response = await api_instance.get(`/org/${org_id}/jobs/recent?${params.toString()}`)
    return response.data
  },

  getRecentWorkspaceJobs: async (org_id: string, ws_id: string, limit: number = 500, month?: string): Promise<RecentJobsResponse> => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month) params.append('month', month)
    const response = await api_instance.get(`/org/${org_id}/ws/${ws_id}/jobs/recent?${params.toString()}`)
    return response.data
  },

  // Utility function that chooses the right API call based on context
  getRecentJobsByContext: async ({ org_id, ws_id }: JobContext, limit: number = 500, month?: string): Promise<RecentJobsResponse> => {
    if (ws_id && org_id) {
      return createJobsApi(api_instance).getRecentWorkspaceJobs( org_id, ws_id, limit, month)
    } else if (org_id) {
      return createJobsApi(api_instance).getRecentOrgJobs( org_id, limit, month)
    } else {
      return createJobsApi(api_instance).getRecentJobs(limit, month)
    }
  }
});